import { useState } from 'react'
import { useContactFields } from '@/api/enrollment-api'
import {
  getBirthDateForNativePicker,
  getMaxBirthDateForDatePicker,
  isUnderageForEnrollment,
  toStoredBirthDate,
  validateEligibilityForm
} from './helpers'
import type { EligibilityFieldErrors } from './types'
import type { EligibilityFormData } from '../types'

type UseEligibilityFormStepArgs = {
  formData: EligibilityFormData
  updateFormData: (data: Partial<EligibilityFormData>) => void
  onContinue: () => void
}

export function useEligibilityFormStep({
  formData,
  updateFormData,
  onContinue
}: UseEligibilityFormStepArgs) {
  const { data: contactFields } = useContactFields()
  const genderOptions = contactFields?.gender ?? []
  const ethnicityOptions = contactFields?.ethnicity ?? []
  const educationOptions = contactFields?.levelOfEducation ?? []
  const [fieldErrors, setFieldErrors] = useState<EligibilityFieldErrors>({})

  const maxBirthDate = getMaxBirthDateForDatePicker()
  const birthDateValue = getBirthDateForNativePicker(formData.dateOfBirth)
  const isUnder18 = isUnderageForEnrollment(formData.dateOfBirth)

  const handleBirthDatePickerChange = (value: string) => {
    if (!value) {
      setFieldErrors(previous => ({ ...previous, dateOfBirth: 'Date of birth is required.' }))
      updateFormData({ dateOfBirth: '' })
      return
    }

    setFieldErrors(previous => ({ ...previous, dateOfBirth: undefined }))
    updateFormData({ dateOfBirth: toStoredBirthDate(value) })
  }

  const handleContinueClick = () => {
    const nextErrors = validateEligibilityForm(formData)
    setFieldErrors(nextErrors)

    if (Object.keys(nextErrors).length > 0 || isUnder18) {
      return
    }

    onContinue()
  }

  const clearFieldError = (field: keyof EligibilityFieldErrors) => {
    setFieldErrors(previous => ({ ...previous, [field]: undefined }))
  }

  return {
    fieldErrors,
    genderOptions,
    ethnicityOptions,
    educationOptions,
    maxBirthDate,
    birthDateValue,
    isUnder18,
    handleBirthDatePickerChange,
    handleContinueClick,
    clearFieldError
  }
}
